import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle  } from 'react';
import { Form, Button, Row, Col, Card, Alert, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import QuotaExhaustedModal from '../views/modals/QuotaExhaustedModal.js';
import { fetch_analysis_running } from '../utils/AnalysisRunning';
import { get_user_error_message } from '../utils/ErrorMessages';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';

import PageData from '../components/PageData';
import AnalysisSummary from '../components/AnalysisSummary';
import AnswerReadiness from '../components/AnswerReadiness';
import RecommendedFixes from '../components/RecommendedFixes';
import StructuralAnalysis from '../components/StructuralAnalysis';
import EntityClarity from '../components/EntityClarity';
import ContentDepth from '../components/ContentDepth';
import Strengths from '../components/Strengths';
import Weaknesses from '../components/Weaknesses';
import ScreenshotThumbnail from '../components/ScreenshotThumbnail';

import TEST_DATA from '../test-data.json';

import './css/aeo-analysis.css';

const num_analysis_stages = 1;

// The quota modal's copy when the user has no credits left for an analysis.
const c_QuotaModalTitle = 'Quota Exhausted';
const c_NoCreditsMessage = 'You’ve used all your free AEO Audited runs for today. You can run another analysis tomorrow';

// The PDF progress is polled once a second; after this many polls the export is given up as failed.
const c_MaxPdfProgressPolls = 180;

// The waiting stages the two progress modals do not show: the stage line stays empty until real work starts.
const c_HiddenAnalysisStages = ['not_started'];
const c_HiddenPdfStages = ['queued'];

// Where the most recent single page analysis is kept between page loads, for a logged in user. The keys name the
// page analysis, since the full-site analysis will keep its own under keys of its own.
const c_PageAnalysisStorageKey = "aeoaudited_page_analysis";
const c_PageAnalysisIdStorageKey = "aeoaudited_page_analysis_id";

// TEMP HACK: login forgets the last page analysis, so the analysis view opens empty after logging in. Remove when the
// page analysis view's first-login behaviour is designed properly.
export const clear_stored_page_analysis = ()=>{
	localStorage.removeItem(c_PageAnalysisStorageKey);
	localStorage.removeItem(c_PageAnalysisIdStorageKey);
};

// The form's headline and subheadline. The public pair is the one free analysis page's pitch for visitors arriving
// from a search; the logged in pair names the tool. public_view picks the pair, so the free analysis page can come
// back by mounting the component with public_view set.
const c_PublicHeadline = 'Check your page for AI-search readiness';
const c_PublicSubheadline = 'Answer Engine Optimization increases the chances that tools like ChatGPT will use your content in their answers.';
const c_LoggedInHeadline = 'Page AEO Analysis';
const c_LoggedInSubheadline = 'Enter a page URL to measure how well AI search tools can read, understand and quote it.';

// Sanity check to make sure post_analysis_request is not sent too many times
let analysis_run_limit = 0;

const SitePageAnalysis = forwardRef((props, ref) => {
    // on_before_run(url), when given, is asked before an analysis starts and returns true when it has taken the url
    // elsewhere (AEO Analysis sends a page of one of the user's sites to its site page view), so none starts here.
    const { enable_analysis_form, url_override, on_analysis_change, on_ask_ai, initial_analysis, public_view, on_before_run } = props;

	const aeoaudited_token = localStorage.getItem(Constants.TOKEN_NAME);
	const navigate = useNavigate();
	
    // TEMP HACK: no default url, so the url field is empty after logging in. Remove with clear_stored_page_analysis.
    const analysis_url = url_override ? url_override : '';

    const [url_input, set_url_input] = useState(analysis_url);
    // const [url_input, set_url_input] = useState('');
    const [error_message, set_error_message] = useState('');
    const [current_stage, set_current_stage] = useState('');
    
    const [user_id, set_user_id] = useState(null);
    const [aeoaudited_browser_uuid, set_aeoaudited_browser_uuid] = useState(null);
    const [showExhaustedModal, setShowExhaustedModal] = useState();

    const [show_structural, set_show_structural] = useState(true);
    const [show_answer, set_show_answer] = useState(true);
    const [show_entity, set_show_entity] = useState(true);
    const [show_depth, set_show_depth] = useState(true);
    const [show_strengths, set_show_strengths] = useState(true);
    const [show_weaknesses, set_show_weaknesses] = useState(true);
    const [show_fixes, set_show_fixes] = useState(true);
    const [show_summary, set_show_summary] = useState(true);
    
    // const [analysis_results, set_analysis_results] = useState(TEST_DATA);
    const [analysis_results, set_analysis_results] = useState(null);
	const [loading, set_loading] = useState(false);
    const [analyzing, set_analyzing] = useState(false);
    // Whether the user has an analysis going anywhere, page or site: they run one at a time, so Run is disabled.
    const [analysis_running, set_analysis_running] = useState(false);
    const [fetching_pdf, set_fetching_pdf] = useState(false);
    // The part of the PDF report the backend is making, shown in the export modal.
    const [pdf_stage, set_pdf_stage] = useState('');

	// The interval polling the running analysis's status. It is stopped when the analysis finishes or fails, when a
	// new analysis is started, and when the component unmounts, so it never keeps polling after the user has left.
	const status_interval_ref = useRef(null);

	const stop_status_polling = ()=>{
		if(status_interval_ref.current){
			clearInterval(status_interval_ref.current);
			status_interval_ref.current = null;
		}
	};

	// The interval polling the PDF report's progress, stopped the same way.
	const pdf_interval_ref = useRef(null);

	const stop_pdf_polling = ()=>{
		if(pdf_interval_ref.current){
			clearInterval(pdf_interval_ref.current);
			pdf_interval_ref.current = null;
		}
	};

	// A logged in user's buttons follow whether they have an analysis going; a public page has no user to ask for.
	const load_analysis_running = async()=>{
		if(!aeoaudited_token){
			return;
		}

		const running = await fetch_analysis_running(aeoaudited_token);
		set_analysis_running(running);
	};

	useEffect(()=>{
		load_analysis_running();

		return ()=>{
			stop_status_polling();
			stop_pdf_polling();
		};
	}, []);

	// A finished analysis the parent already has (a site page opens on its latest one) is shown without a new run.
	// Its id is stored as the PDF export expects.
	useEffect(()=>{
		if(initial_analysis){
			localStorage.setItem(c_PageAnalysisIdStorageKey, initial_analysis.id);
			set_analysis_results(initial_analysis);
		}
	}, [initial_analysis]);

	const is_logged_in = ()=>{
		return !!localStorage.getItem(Constants.TOKEN_NAME);
	};

	// A logged in user's most recent page analysis comes back after a page refresh. A page given its analysis by the
	// parent, or a fixed url, does not restore one.
	useEffect(()=>{
		if(initial_analysis || url_override || !is_logged_in()){
			return;
		}

		const stored_analysis_text = localStorage.getItem(c_PageAnalysisStorageKey);
		if(!stored_analysis_text){
			return;
		}

		try {
			const stored_analysis = JSON.parse(stored_analysis_text);
			if(stored_analysis && stored_analysis.id && stored_analysis.scoring){
				localStorage.setItem(c_PageAnalysisIdStorageKey, stored_analysis.id);
				if(stored_analysis.url){
					set_url_input(stored_analysis.url);
				}

				set_analysis_results(stored_analysis);
			}
		}
		catch (error) {
			console.log('SitePageAnalysis/restore_page_analysis - ERROR: ', describe_error(error));
			localStorage.removeItem(c_PageAnalysisStorageKey);
		}
	}, []);

	// Tell the parent which analysis is on screen (the analysis page passes it to the assistant panel).
	useEffect(()=>{
		if(on_analysis_change){
			on_analysis_change(analysis_results);
		}
	}, [analysis_results]);

    var getting_analysis_status = false;

	useEffect(()=>{
        var aeoaudited_browser_uuid = localStorage.getItem("aeoaudited_browser_uuid");
        if(!aeoaudited_browser_uuid){
            aeoaudited_browser_uuid = crypto.randomUUID();
            localStorage.setItem("aeoaudited_browser_uuid", aeoaudited_browser_uuid);
        }

        set_aeoaudited_browser_uuid(aeoaudited_browser_uuid);		
	});

    const format_stage_name = (text)=>{
        if(!text){
            return "";
        }

        return text
            .split("_")
            .map((w)=> w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    };

    const validate_url = (value)=>{
        try{
            let url = value.trim();

            if(url_override){
                url = url_override;
            }

            if(!/^https?:\/\//i.test(url)){
                url = "https://" + url;
            }

            const parsed = new URL(url);
            if(parsed.protocol !== "http:" && parsed.protocol !== "https:"){
                return false;
            }

            return parsed.toString();
        }catch(error){
            return false;
        }
    };

    const render_loading_modal = ()=>{
        const is_finished = (current_stage == 'finished');
        const show_stage = !is_finished && current_stage && !c_HiddenAnalysisStages.includes(current_stage);
        return (
            <Modal
                show={loading}
                backdrop="static"
                centered
                keyboard={false}>
                <Modal.Body className="loading-modal-body">
                    <Spinner animation="border" role="status" />
                    <div className='modal-running-analysis'>
                        Running analysis...
                    </div>

                    {show_stage && <div className="loading-stage-text">
                        {format_stage_name(current_stage)}
                    </div>}
                </Modal.Body>
            </Modal>
        );
    };

    const render_pdf_modal = ()=>{
        return (
            <Modal
                show={fetching_pdf}
                backdrop="static"
                centered
                keyboard={false}>
                <Modal.Body className="loading-modal-body">
                    <Spinner animation="border" role="status" />
                    <div className='modal-running-analysis'>
                        Generating PDF report...
                    </div>

                    {(pdf_stage && !c_HiddenPdfStages.includes(pdf_stage)) && <div className="loading-stage-text">
                        {format_stage_name(pdf_stage)}
                    </div>}
                </Modal.Body>
            </Modal>
        );
    };

    useImperativeHandle(ref, () => ({
        send_form
    }));

    // Starts an analysis of the url. event is the form's or button's event, or none when a view starts the analysis
    // itself.
    const send_form = async(event)=> {
        if(event){
            event.preventDefault();
        }

        set_error_message('');
        if(!validate_url(url_input)){
            set_error_message('Invalid URL');
            return;
        }

        if(on_before_run && await on_before_run(url_input)){
            return;
        }

        // set_loading(true);
        // set_analysis_results(null);

        set_show_structural(true);
        set_show_answer(true);
        set_show_entity(true);
        set_show_depth(true);
        set_show_strengths(true);
        set_show_weaknesses(true);
        set_show_fixes(true);
        set_show_summary(true);

        // const test_interval = setInterval(()=>{
        //     set_loading(false);
        //     set_analysis_results(TEST_DATA);
        //     clearInterval(test_interval);
        // }, 3000);

        // DO NOT REMOVE ANY COMMENTS
        // clear any stored analysis_id before starting a new run
        localStorage.removeItem(c_PageAnalysisIdStorageKey);
        localStorage.removeItem(c_PageAnalysisStorageKey);

        // first stage call (no analysis_id)
        post_analysis_request('/run_aeo_audit');
    };

    const get_request = async(endpoint, analysis_id=null) => {        
        try{
            const url = Constants.SERVER_URL + '/api' + endpoint;
            const headers = { authorization: aeoaudited_token };
            
            const response = await axios.get(url, {headers});
            return response;

        }catch(error){
            console.log('error: ', describe_error(error));
        }
    }

    const on_analysis_finished = async(response)=>{
        const end = new Date();
        var analysis_start = localStorage.getItem("aeoaudited_analysis_start");
        if(analysis_start){
            analysis_start = new Date(analysis_start);
            const duration = end.getTime() - analysis_start.getTime();
            localStorage.setItem("aeoaudited_analysis_duration", duration);
            const duration_seconds = parseInt(duration) / 1000.0;
            console.log('duration: ' + duration_seconds.toFixed(2) + " seconds");
        }

        set_analyzing(false);
        set_loading(false);
        set_analysis_results(response.data.analysis);
        analysis_run_limit = 0;
        load_analysis_running();

        // The finished analysis is kept for a logged in user, so a page refresh shows it again.
        if(is_logged_in()){
            localStorage.setItem(c_PageAnalysisStorageKey, JSON.stringify(response.data.analysis));
        }
    }

    const post_analysis_request = async(endpoint, analysis_id=null) => {
        const url = Constants.SERVER_URL + '/api' + endpoint;
        console.log('url: ', url);

        set_analyzing(true);
        set_analysis_results(null);

        var num_runs = analysis_run_limit;
        if(num_runs > 7){
            return;
        }

        var final_url = url_input;
        if(final_url.indexOf('https://')  == -1){
            final_url = 'https://' + final_url;
        }

        const email = localStorage.getItem(Constants.USER_EMAIL);
        const user_id = localStorage.getItem(Constants.USER_ID);

        const payload = {
            url: final_url,
            user_id,
            email,
            browser_uuid: aeoaudited_browser_uuid // Make sure this is sent in every request
        };

        // Add analysis_id only after the first run
        if(analysis_id){
            payload.analysis_id = analysis_id;
        }

        try{
            localStorage.setItem("aeoaudited_analysis_start", new Date());

            const headers = { authorization: aeoaudited_token };
            const response = await axios.post(url, payload, {headers});
            set_loading(true);
            set_analysis_results(null);
            
            // backend returns: { analysis_id, done, analysis }
            const analysis_id = response.data.analysis.id;
            const done = response.data.done;

            if(response.data.analysis.stage){
                set_current_stage(response.data.analysis.stage);
            }

            // Store the analysis_id so you can resume
            localStorage.setItem(c_PageAnalysisIdStorageKey, analysis_id);

            if(analysis_id){
				stop_status_polling();
				status_interval_ref.current = setInterval(async () =>{
                    const status_response = await get_request('/analysis_status/' + analysis_id);
                    if(status_response && 
                        status_response.data && 
                        status_response.data.analysis){
                        const analysis = status_response.data.analysis;
                        set_current_stage(analysis.stage);
                        
                        console.log('analysis.stage: ', analysis.stage);
                        if(analysis.stage == 'finished'){
							stop_status_polling();
                            await on_analysis_finished(status_response);
                        }
						else
						if(analysis.stage == 'failed'){
							stop_status_polling();
							set_analyzing(false);
							set_loading(false);
							set_error_message('The analysis could not be completed. Please try again.');
							load_analysis_running();
						}
                    }
                }, 1000);
            }

            analysis_run_limit = num_runs + 1;

        }catch(error){
            set_analyzing(false);
            set_loading(false);
            set_error_message(get_user_error_message(error, ''));
            load_analysis_running();

            if(error.response && error.response.status === 403) {
                // setError("You have reached your daily limit for free analysis runs.");
                show_exhaustion_modal ();
                return;
            }
        }
    };

    const on_input_change = (input_value)=>{
        if(validate_url(input_value)){
            set_error_message('');
        }
        
        set_url_input(input_value); 
    }

    // The report's filename from the download response's Content-Disposition header.
    const get_download_filename = (response)=>{
        const content_disposition = response.headers['content-disposition'];
        if(content_disposition){
            const match = content_disposition.match(/filename="([^"]+)"/);
            if(match){
                return match[1];
            }
        }

        return 'AEOAudited_report.pdf';
    };

    // Hands the PDF to the browser as a download under the given filename. The browser shows a save-as dialog when
    // the user has "Ask where to save each file" on; otherwise it saves straight to the downloads folder.
    const save_pdf = (pdf_blob, filename)=>{
        const object_url = window.URL.createObjectURL(pdf_blob);
        const link = document.createElement('a');
        link.href = object_url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(object_url);
    };

    // Downloads the finished report and hands it to the browser to save.
    const download_pdf = async(analysis_id)=>{
        const download_url = Constants.SERVER_URL + '/api/pdf_report_download/' + analysis_id;
        const headers = { authorization: aeoaudited_token };
        const response = await axios.get(download_url, { headers, responseType: 'blob' });
        const pdf_blob = new Blob([response.data], { type: 'application/pdf' });
        save_pdf(pdf_blob, get_download_filename(response));
    };

    const on_pdf_export_failed = (error)=>{
        stop_pdf_polling();
        set_fetching_pdf(false);
        set_pdf_stage('');
        console.log('on_pdf_export - ERROR: ', describe_error(error));

        if(error.response && error.response.status === 403) {
            show_exhaustion_modal ();
            return;
        }

        set_error_message('The PDF report could not be generated. Please try again.');
    };

    // Queues the report on the backend, polls its progress every second and, once it is done, downloads it
    // through the save-as dialog.
    const on_pdf_export = async(event)=>{
        event.preventDefault();

        set_error_message('');
        const analysis_id = localStorage.getItem(c_PageAnalysisIdStorageKey);
        if(!analysis_id){
            set_error_message('There is no analysis to export.');
            return;
        }

        try{
            set_pdf_stage('');
            set_fetching_pdf(true);
            const user_id = localStorage.getItem(Constants.USER_ID);
            const url =  Constants.SERVER_URL + '/api/pdf_report/' + analysis_id;

            // Make sure aeoaudited_browser_uuid is sent in every request
            const payload = { url, user_id, browser_uuid: aeoaudited_browser_uuid};
            await axios.post(url, payload);

            const progress_url =  Constants.SERVER_URL + '/api/get_pdf_report_progress/' + analysis_id;
            const progress_payload = { user_id, browser_uuid: aeoaudited_browser_uuid};
            const headers = { authorization: aeoaudited_token };

            // A report that fails on the backend leaves nothing to poll for, so the poll gives up after a limit.
            var num_polls = 0;

            stop_pdf_polling();
            pdf_interval_ref.current = setInterval(async()=>{
                try{
                    ++num_polls;
                    if(num_polls > c_MaxPdfProgressPolls){
                        throw new Error('PDF report timed out');
                    }

                    const progress_response = await axios.post(progress_url, progress_payload, { headers });
                    if(!progress_response || !progress_response.data){
                        return;
                    }

                    const {progress, stage} = progress_response.data;
                    if(stage){
                        set_pdf_stage(stage);
                    }

                    if(progress == 100){
                        stop_pdf_polling();
                        set_pdf_stage('downloading');
                        await download_pdf(analysis_id);
                        set_fetching_pdf(false);
                        set_pdf_stage('');
                    }
                }catch(error){
                    on_pdf_export_failed(error);
                }
            }, 1000);

        }catch(error){
            on_pdf_export_failed(error);
        }
    }

    const on_close_exhaustion_modal = ()=>{
        setShowExhaustedModal(false);
    }

    const show_exhaustion_modal = ()=>{
        setShowExhaustedModal(true);
    }
  
    const render_modal = ()=>{    
        if(showExhaustedModal){
			return (
				<QuotaExhaustedModal
					title={c_QuotaModalTitle}
					message={c_NoCreditsMessage}
					on_close={on_close_exhaustion_modal}/>
			);
        }
    }

    // The public pitch, large and centered, or the logged in tool name, compact and in line with the form.
    const render_form_heading = ()=>{
        if(public_view){
            return (
                <React.Fragment>
                    <h1 className='aeo-title'>{c_PublicHeadline}</h1>
                    <p className='aeo-description'>{c_PublicSubheadline}</p>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <h1 className='aeo-title aeo-title-compact'>{c_LoggedInHeadline}</h1>
                <p className='aeo-description aeo-description-compact'>{c_LoggedInSubheadline}</p>
            </React.Fragment>
        );
    };

    // The thumbnail of the screenshot taken when the page was analyzed, on the standalone analysis page. A site
    // page view shows it in its own header instead. Logged in users only: the image is fetched with the user's
    // token.
    const render_screenshot = ()=>{
        if(public_view || !enable_analysis_form || !analysis_results || !analysis_results.screenshot_thumbnail_image_id){
            return null;
        }

        return (
            <div className="analysis-screenshot-row">
                <ScreenshotThumbnail image_id={analysis_results.screenshot_thumbnail_image_id} alt_text="Screenshot of the analyzed page"/>
            </div>
        );
    };

    const render_analysis = ()=>{
        return   <React.Fragment>
                    {enable_analysis_form && <React.Fragment>
                        {render_form_heading()}
                        <Form as="form" className="aeo-form" onSubmit={send_form}>
                            <div className="analysis-input-wrap">
                                <input
                                    type="text"
                                    className="analysis-url-input"
                                    value={url_input}
                                    onChange={(e)=>{on_input_change(e.target.value)}}
                                    placeholder="Enter URL to analyze"
                                />
                                <Button variant="primary" 
                                        type="submit"
                                        className="analysis-page-button"
                                        disabled={analyzing || analysis_running || fetching_pdf}>
                                    Run
                                </Button>
                            </div>
                                    
                            {error_message ? (
                                <div className="analysis-error-message">
                                    {error_message}
                                </div>
                            ) : null}
                        </Form>
                    </React.Fragment>}

					{(!enable_analysis_form && error_message) ? (
						<div className="analysis-error-message">
							{error_message}
						</div>
					) : null}

                    {render_screenshot()}
                    <AnalysisSummary analysis_results={analysis_results} on_pdf_export={on_pdf_export} analyzing={analyzing} fetching_pdf={fetching_pdf} on_ask_ai={on_ask_ai} show_summary={show_summary} set_show_summary={set_show_summary}/>
                    <RecommendedFixes analysis_results={analysis_results} show_fixes={show_fixes} set_show_fixes={set_show_fixes} />
                    <AnswerReadiness analysis_results={analysis_results} show_answer={show_answer} set_show_answer={set_show_answer} />
                    <EntityClarity analysis_results={analysis_results} show_entity={show_entity} set_show_entity={set_show_entity} />
                    <ContentDepth analysis_results={analysis_results} show_depth={show_depth} set_show_depth={set_show_depth} />
                    <Strengths analysis_results={analysis_results} show_strengths={show_strengths} set_show_strengths={set_show_strengths} />
                    <Weaknesses analysis_results={analysis_results} show_weaknesses={show_weaknesses} set_show_weaknesses={set_show_weaknesses} />
                    <StructuralAnalysis analysis_results={analysis_results} show_structural={show_structural} set_show_structural={set_show_structural} />

                    {render_modal()}
                 </React.Fragment>
    }

    return  <React.Fragment>
                {render_loading_modal()}
                {render_pdf_modal()}
                {render_analysis()}
            </React.Fragment>
	
});

export default SitePageAnalysis;

